<script setup lang="ts">
/**
 * LandingAxisCard — 랜딩 축 카드 1장.
 *
 * ── 무게(weight)는 `axis.flow` 에서 파생 ──
 * 카드 5장을 같은 형태에 색만 바꿔 찍으면 깔때기 구조가 밋밋해진다. **배치 역할이 곧 시각 위계**가
 * 되도록 단계마다 카드 형태 자체를 다르게 한다 — 스크롤하며 무게가 변하는 리듬이 생기고, 그 리듬이
 * 깔때기 의미(입구 → 갈래 → 수렴)와 일치한다.
 *
 *  | flow       | 무게   | 처리                                                   |
 *  |------------|--------|--------------------------------------------------------|
 *  | `source`   | 다크   | `--brand` 네이비 면 · 흰 글자 · 액센트 칩 — 깔때기 입구  |
 *  | `analysis` | 라이트 | 흰 면 · 좌측 `tone` 컬러 레일 — 나란한 선택지라 가볍게   |
 *
 * 색 분기는 루트 클래스의 `--axis-c`(레일·아이콘·체크)·`--axis-fg`(본문 글자)·`--axis-dim`(보조 글자)
 * 세 변수 한 곳에서만 한다. 무게별 면/테두리는 각 variant 클래스가 소유.
 *
 * ── 구성 ──
 *  - 헤드: STEP 배지(`axis.step`) + 아이콘 + 축명 + 태그 칩. 원본의 거대 고스트 숫자·컬러 밴드는 쓰지 않는다.
 *  - 본문: 한 줄 설명 → 헤어라인 → 하위 기능(체크 그리드 — 전폭 카드 3열, 일반 2열).
 *  - open: <button> — hover 리프트·이동 화살표·go emit. soon: 비인터랙티브 <div>(클릭 무동작, 034 FR-007).
 *  - `flow='source'`: 전폭(부모 그리드 `grid-column: 1 / -1`).
 *
 * 적재 바(`flow='archive'`)는 카드가 아니라 LandingArchiveBar 가 그린다 — 여기 오지 않는다.
 * 이동·게이트는 페이지 소유 — 여기서는 `LandingGoTarget` 만 올린다.
 */
import {
  Archive,
  ArrowRight,
  Check,
  ClipboardList,
  ListChecks,
  Target,
  type LucideIcon,
} from 'lucide-vue-next';
import { computed } from 'vue';

import type { LandingAxis, LandingAxisIcon, LandingGoTarget } from '#/data/analysis/landingAxes';

const props = defineProps<{ axis: LandingAxis }>();

defineEmits<{
  /** open 축 클릭 — 페이지가 게이트 판정 후 대표 route 로 이동. */
  go: [target: LandingGoTarget];
}>();

/** 아이콘 키 → lucide 컴포넌트(workspaceNav 아이콘 키). */
const ICON_MAP: Record<LandingAxisIcon, LucideIcon> = {
  clipboard: ClipboardList,
  target: Target,
  'list-checks': ListChecks,
  archive: Archive,
};

/** 전폭 카드 — 깔때기의 입구(source)만 행 전체를 차지한다(분석 행이 1장이면 부모 그리드가 전폭으로 편다). */
const wide = computed(() => props.axis.flow === 'source');

/** 무게 클래스 — 배치 역할에서 파생(축 key 하드코딩 금지). */
const weightClass = computed(() => `ws-axis--${props.axis.flow}`);

/** STEP 배지 표기 — 2자리 고정(01·02·03)이라 단계가 나란히 읽힌다. */
const stepLabel = computed(() => String(props.axis.step ?? '').padStart(2, '0'));
</script>

<template>
  <component
    :is="axis.status === 'open' ? 'button' : 'div'"
    :type="axis.status === 'open' ? 'button' : undefined"
    class="ws-axis"
    :class="[
      weightClass,
      axis.tone ? `ws-axis--t${axis.tone}` : '',
      { 'ws-axis--wide': wide, 'ws-axis--go': axis.status === 'open' },
    ]"
    @click="axis.status === 'open' && $emit('go', { name: axis.name, route: axis.route })"
  >
    <span class="ws-axis-head">
      <span class="ws-axis-icon">
        <component :is="ICON_MAP[axis.icon]" :size="wide ? 22 : 20" />
      </span>

      <span class="ws-axis-titles">
        <span class="ws-axis-meta">
          <span v-if="axis.step" class="ws-axis-step">STEP {{ stepLabel }}</span>
          <span class="ws-axis-chip">{{ axis.status === 'soon' ? '준비중' : axis.tag }}</span>
        </span>
        <span class="ws-axis-name">{{ axis.name }}</span>
      </span>

      <span v-if="axis.status === 'open'" class="ws-axis-arrow"><ArrowRight :size="18" /></span>
    </span>

    <span class="ws-axis-body">
      <span class="ws-axis-one">{{ axis.one }}</span>

      <ul v-if="axis.sub" class="ws-axis-subs">
        <li v-for="s in axis.sub" :key="s" class="ws-axis-sub">
          <span class="ws-axis-check"><Check :size="12" :stroke-width="3" /></span>
          <span>{{ s }}</span>
        </li>
      </ul>
    </span>
  </component>
</template>

<style scoped>
/*
 * ── 강조 색(--axis-c) — 레일·아이콘·체크. 무게와 무관하게 tone 이 고른다. ──
 * 다크·틴트 카드는 면이 진하거나 액센트라 step 색이 묻힌다 → 아래 variant 블록에서 덮어쓴다.
 */
.ws-axis--t1 {
  --axis-c: var(--step-1);
}

.ws-axis--t2 {
  --axis-c: var(--step-2);
}

.ws-axis--t3 {
  --axis-c: var(--step-3);
}

.ws-axis--t4 {
  --axis-c: var(--step-4);
}

.ws-axis--t5 {
  --axis-c: var(--step-5);
}

.ws-axis {
  /* 기본(라이트) 글자 위계 — 무게 variant 가 필요한 것만 덮는다. */
  --axis-fg: var(--brand-ink);
  --axis-dim: var(--body);

  position: relative;
  grid-column: span 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  padding: 20px 22px 22px;
  overflow: hidden;
  text-align: left;
  color: var(--axis-fg);
  border-radius: var(--radius-card);
}

/* ── 1단 입구 — 다크 면. 깔때기에서 가장 무겁게. ── */
.ws-axis--source {
  --axis-fg: var(--bg);
  --axis-dim: hsl(var(--brand-h) 14% 82%);
  --axis-c: var(--accent-2);

  background: var(--brand);
  border: 1px solid var(--brand);
  box-shadow: var(--shadow-md);
}

/* ── 2단 갈래 — 흰 면 + 좌측 컬러 레일. 나란한 선택지라 가볍게. ── */
.ws-axis--analysis {
  background: var(--bg);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-sm);
}

/*
 * 레일 — 카드 좌측을 세로로 긋는 컬러 띠(tone 이 두 축을 가른다).
 * 4px + 좌측 패딩 추가분으로 본문과 띄운다 — 3px 를 테두리에 붙이면 진한 tone(step-2)이
 * 카드 선과 뭉쳐 띠로 안 읽힌다(실측).
 */
.ws-axis--analysis {
  padding-left: 26px;
}

.ws-axis--analysis::before {
  content: '';
  position: absolute;
  inset-block: 0;
  left: 0;
  width: 4px;
  background: var(--axis-c);
}

/* ── 3단 수렴점 — 액센트 테두리 + 옅은 틴트 면. ── */

/*
 * 상단 액센트 엣지 — 틴트만으로는 흰 카드와 명도차가 작아(L88~95%) 강조가 약하다.
 * 깔때기의 도착점이라는 신호를 선 하나로 못박는다.
 */

/* ── 인터랙션 — open 카드만(리프트·포커스 링) ── */
.ws-axis--go {
  cursor: pointer;
  transition:
    border-color 0.16s,
    box-shadow 0.16s,
    transform 0.16s;
}

.ws-axis--go:hover {
  transform: translateY(-2px);
}

.ws-axis--analysis.ws-axis--go:hover {
  border-color: var(--axis-c);
  box-shadow: var(--shadow-md);
}

.ws-axis--source.ws-axis--go:hover,
.ws-axis--go:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.ws-axis--wide {
  grid-column: 1 / -1;
}

/* ── 헤드 — 아이콘 · (STEP 배지 + 칩) / 축명 · 화살표 ── */
.ws-axis-head {
  display: flex;
  align-items: flex-start;
  gap: 13px;
}

.ws-axis-icon {
  display: grid;
  place-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: 11px;

  /* 강조색을 면에 옅게 깔고 아이콘은 본색 — 색면을 줄이고 포인트만 남긴다. */
  background: color-mix(in srgb, var(--axis-c) 13%, transparent);
  color: var(--axis-c);
}

.ws-axis--source .ws-axis-icon {
  background: hsl(0deg 0% 100% / 12%);
}

.ws-axis-titles {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  flex: 1 1 auto;
}

/*
 * 전폭 카드는 헤드가 가로로 길어 화살표가 우측 끝까지 밀린다 — 제목 바로 뒤에 붙여
 * "이 축으로 간다"는 신호가 제목과 한 덩어리로 읽히게 한다.
 */
.ws-axis--wide .ws-axis-titles {
  flex: 0 1 auto;
}

.ws-axis--wide .ws-axis-head::after {
  content: '';
  flex: 1 1 auto;
}

.ws-axis-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
}

/* STEP 배지 — 원본의 82px 고스트 숫자를 대체하는 소형 표기(2자리 고정) */
.ws-axis-step {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--axis-c);
  font-variant-numeric: tabular-nums;
}

.ws-axis--source .ws-axis-step {
  color: var(--accent-2);
}

.ws-axis-chip {
  display: inline-block;
  padding: 2px 9px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.5;
  color: var(--axis-dim);
  background: color-mix(in srgb, var(--axis-c) 9%, transparent);
  border-radius: 999px;
}

.ws-axis--source .ws-axis-chip {
  color: var(--bg);
  background: hsl(0deg 0% 100% / 14%);
}

.ws-axis-name {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.35;
}

.ws-axis--wide .ws-axis-name {
  font-size: 20px;
}

.ws-axis-arrow {
  display: grid;
  place-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border-radius: 50%;
  color: var(--axis-dim);
  transition:
    background 0.16s,
    color 0.16s;
}

.ws-axis--go:hover .ws-axis-arrow {
  color: var(--axis-c);
  background: color-mix(in srgb, var(--axis-c) 12%, transparent);
}

.ws-axis--source.ws-axis--go:hover .ws-axis-arrow {
  color: var(--bg);
  background: hsl(0deg 0% 100% / 16%);
}

/* ── 본문 ── */
.ws-axis-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 13px;
}

.ws-axis-one {
  font-size: 13.5px;
  line-height: 1.7;
  color: var(--axis-dim);
}

.ws-axis--wide .ws-axis-one {
  font-size: 14.5px;
}

/* 하위 기능 — 체크 그리드(원본 도트 불릿 대체). 설명과 헤어라인으로 나눈다. */
.ws-axis-subs {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px 18px;
  margin: 0;
  padding: 13px 0 0;
  border-top: 1px solid;

  /* 다크·틴트 면에서도 선이 보이도록 현재 글자색을 옅게 쓴다(고정 회색선은 묻힌다). */
  border-color: color-mix(in srgb, currentcolor 13%, transparent);
}

@media (width >= 480px) {
  .ws-axis-subs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* 전폭 카드는 하위 기능이 한 줄에 들어가게 3열까지 */
@media (width >= 1080px) {
  .ws-axis--wide .ws-axis-subs {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.ws-axis-sub {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--axis-dim);
}

.ws-axis-check {
  display: grid;
  place-content: center;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: color-mix(in srgb, var(--axis-c) 15%, transparent);
  color: var(--axis-c);
}

.ws-axis--source .ws-axis-check {
  background: hsl(0deg 0% 100% / 16%);
  color: var(--bg);
}
</style>
